var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { format } from "date-fns";
import { WebhookConfigurationStatus } from "healthex-common/webhook.dtos";
import { action, computed, observable } from "mobx";
import { Disposer } from "../Infra/Disposer";
import { NOT_LOADED } from "../Infra/RemoteData";
import { NamedRoutes } from "./NamedRoutes";
import { SimpleDialogModel } from "./SimpleDialogModel";
export class AdminWebhookDetailViewModel {
    constructor(opts) {
        this.loadedWebhook = NOT_LOADED;
        this.secretRevealState = null;
        this.editWebhookDialogOpen = false;
        this.deleteConfirmationDialogModel = new SimpleDialogModel();
        this.rotateConfirmationDialogModel = new SimpleDialogModel();
        this.bannerUIManager = opts.container.bannerUIManager;
        this.provisioningService = opts.container.provisioningService;
        this.authManager = opts.container.authManager;
        this.routingUIManager = opts.container.routingUIManager;
        this.disposer = new Disposer();
    }
    run() {
        this.disposer.reaction(() => { var _a; return (_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.webhookId; }, () => void this.loadWebhook(), { fireImmediately: true, name: "loadWebhookOnDetailRouteChange" });
    }
    stop() {
        this.disposer.dispose();
    }
    get webhookId() {
        var _a, _b;
        return (_b = (_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.webhookId) !== null && _b !== void 0 ? _b : null;
    }
    get activeTab() {
        var _a;
        const tab = (_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.tab;
        if (tab === "deliveries") {
            return "DELIVERIES";
        }
        else {
            return "DETAILS";
        }
    }
    async loadWebhook(skipLoadingState = false) {
        if (!skipLoadingState)
            this.loadedWebhook = NOT_LOADED;
        if (!this.authManager.orgId || !this.webhookId)
            return;
        this.loadedWebhook = await this.provisioningService.getWebhookConfiguration(this.authManager.orgId, this.webhookId);
    }
    get webhook() {
        return this.loadedWebhook;
    }
    get projectScope() {
        var _a, _b;
        if (this.loadedWebhook === NOT_LOADED)
            return null;
        return {
            orgWide: this.loadedWebhook.studyScope === null,
            projects: (_b = (_a = this.loadedWebhook.studyScopeProjects) === null || _a === void 0 ? void 0 : _a.map((project) => project.title)) !== null && _b !== void 0 ? _b : [],
        };
    }
    get projectScopeModeLabel() {
        const scope = this.projectScope;
        if (!scope)
            return "";
        if (scope.orgWide)
            return "organization-wide";
        return scope.projects.length === 1 ? "single project" : `${scope.projects.length} projects`;
    }
    get createdLabel() {
        if (this.loadedWebhook === NOT_LOADED)
            return null;
        return format(this.loadedWebhook.createdAt, "MMM d, yyyy · h:mm a");
    }
    get maskedSecret() {
        if (this.loadedWebhook === NOT_LOADED)
            return null;
        return `whsec_${"\u2022".repeat(40)}${this.loadedWebhook.webhookSecretLast4}`;
    }
    get lastDeliveryLabel() {
        if (this.loadedWebhook === NOT_LOADED)
            return null;
        return this.loadedWebhook.lastDeliveryLogTimestamp
            ? this.loadedWebhook.lastDeliveryLogTimestamp.toLocaleString(undefined, {
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
            })
            : null;
    }
    setActiveTab(tab) {
        if (!this.webhookId) {
            return;
        }
        this.routingUIManager.navigateTo(NamedRoutes.ADMIN_WEBHOOK_DETAIL, {
            webhookId: this.webhookId,
            ...(tab !== "DETAILS" && { tab: tab.toLowerCase() }),
        });
    }
    backToWebhooks() {
        this.routingUIManager.navigateTo(NamedRoutes.ADMIN_WEBHOOKS);
    }
    async rotateSecret() {
        if (this.loadedWebhook === NOT_LOADED || !this.authManager.orgId)
            return;
        const confirmed = await this.rotateConfirmationDialogModel.showAndWait();
        if (!confirmed)
            return;
        const rotateResult = await this.provisioningService.rotateWebhookSecret(this.authManager.orgId, this.loadedWebhook.id);
        this.secretRevealState = {
            kind: "rotated",
            webhookName: this.loadedWebhook.name,
            signingSecret: rotateResult.webhookSecret,
            testEventSent: false,
        };
        void this.loadWebhook(true);
    }
    closeSecretReveal() {
        this.secretRevealState = null;
    }
    async sendTestEvent() {
        if (this.loadedWebhook === NOT_LOADED)
            return;
        if (!this.authManager.orgId)
            return;
        try {
            await this.provisioningService.testWebhookDelivery(this.authManager.orgId, this.loadedWebhook.id);
            this.bannerUIManager.displayBanner({ text: `Test event sent for "${this.loadedWebhook.name}"` });
        }
        catch (error) {
            this.bannerUIManager.displayBanner({ text: `Error sending test event for "${this.loadedWebhook.name}"`, type: "ERROR" });
        }
    }
    editWebhook() {
        if (this.loadedWebhook === NOT_LOADED)
            return;
        this.editWebhookDialogOpen = true;
    }
    closeEditWebhook(updated) {
        this.editWebhookDialogOpen = false;
        if (updated) {
            void this.loadWebhook(true);
        }
    }
    async pauseOrResume() {
        if (this.loadedWebhook === NOT_LOADED || !this.authManager.orgId)
            return;
        await this.provisioningService.updateWebhookConfiguration(this.authManager.orgId, this.loadedWebhook.id, {
            status: this.loadedWebhook.status === WebhookConfigurationStatus.ACTIVE
                ? WebhookConfigurationStatus.PAUSED
                : WebhookConfigurationStatus.ACTIVE,
        });
        void this.loadWebhook(true);
    }
    async deleteWebhook() {
        if (this.loadedWebhook === NOT_LOADED || !this.authManager.orgId)
            return;
        const confirmed = await this.deleteConfirmationDialogModel.showAndWait();
        if (!confirmed)
            return;
        const name = this.loadedWebhook.name;
        await this.provisioningService.deleteWebhookConfiguration(this.authManager.orgId, this.loadedWebhook.id);
        this.bannerUIManager.displayBanner({ text: `Webhook "${name}" successfully deleted` });
        this.backToWebhooks();
    }
}
__decorate([
    observable
], AdminWebhookDetailViewModel.prototype, "loadedWebhook", void 0);
__decorate([
    observable
], AdminWebhookDetailViewModel.prototype, "secretRevealState", void 0);
__decorate([
    observable
], AdminWebhookDetailViewModel.prototype, "editWebhookDialogOpen", void 0);
__decorate([
    computed
], AdminWebhookDetailViewModel.prototype, "webhookId", null);
__decorate([
    computed
], AdminWebhookDetailViewModel.prototype, "activeTab", null);
__decorate([
    computed
], AdminWebhookDetailViewModel.prototype, "webhook", null);
__decorate([
    computed
], AdminWebhookDetailViewModel.prototype, "projectScope", null);
__decorate([
    computed
], AdminWebhookDetailViewModel.prototype, "projectScopeModeLabel", null);
__decorate([
    computed
], AdminWebhookDetailViewModel.prototype, "createdLabel", null);
__decorate([
    computed
], AdminWebhookDetailViewModel.prototype, "maskedSecret", null);
__decorate([
    computed
], AdminWebhookDetailViewModel.prototype, "lastDeliveryLabel", null);
__decorate([
    action
], AdminWebhookDetailViewModel.prototype, "setActiveTab", null);
